import { useMemo, useState } from "react";
import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserCheck, XCircle, CreditCard, Fuel, ShieldCheck, Car, Clock, MapPin, Wrench, Printer, Search, X, MessageCircle, Mail } from "lucide-react";

const SECTIONS = [
  {
    icon: UserCheck,
    title: "Age & Driver's License",
    testid: "policy-age",
    items: [
      "Renters must be at least 25 years of age; no exceptions are permitted.",
      "A valid driver's license, held for a minimum of one (1) year, is required.",
      "A clear photograph of your driver's license must be uploaded at the time of booking.",
      "International visitors may operate a vehicle with a valid license from their country of residence for up to three (3) months.",
      "Additional drivers must be at least 25 years of age, registered with Easy Drive Car Rental, and present their own valid driver's license.",
    ],
  },
  {
    icon: XCircle,
    title: "Cancellation & Refunds",
    testid: "policy-cancellation",
    items: [
      "All cancellations are subject to a 20% cancellation charge, regardless of the notice period provided.",
      "Your first reschedule is free when requested more than 24 hours before pickup. A second reschedule carries a 20% fee. A third reschedule request automatically cancels the booking under the standard cancellation policy.",
      "Cancellations made within 24 hours of the scheduled pickup time are non-refundable.",
      "No-shows: failure to collect the reserved vehicle without prior notification will result in forfeiture of the full rental amount, and no refund will be issued.",
      "Early returns: refunds are not issued for unused rental days; the renter remains responsible for the full rental period as booked.",
      "Reservation modifications, including changes to dates or vehicle, incur a $30.00 modification fee.",
      "Vehicle upgrades are offered subject to availability; the renter is responsible for the price difference plus a $30.00 upgrade service fee, added to the final invoice.",
      "Approved refunds are processed to the original payment method within 5–10 business days.",
    ],
  },
  {
    icon: CreditCard,
    title: "Payments",
    testid: "policy-payments",
    items: [
      "We accept major credit and debit cards through our secure online checkout, as well as PayPal and Zelle.",
      "A refundable security deposit of $150.00 is required on all vehicles, without exception.",
      "A 5% processing fee applies to all rental bookings, extensions, upgrades, and post-rental charges.",
      "Bookings paid via PayPal or Zelle are confirmed once payment has been verified; please include your booking reference in the payment note.",
      "A valid payment card may be required at pickup to authorize the security deposit and damage deductible.",
      "Paradise Island bridge toll pass usage carries an $8.99 service fee per rental period plus $3.00 per trip over the bridge. Trips are recorded during the rental and added to the final invoice upon return of the vehicle.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Insurance & Damage",
    testid: "policy-insurance",
    items: [
      "Basic insurance coverage is included with every rental.",
      "The renter is responsible for the first $500.00 of any damage claim (the deductible).",
      "Insurance coverage is voided by off-road use, driving under the influence, or operation of the vehicle by an unregistered driver.",
      "All damage must be reported to Easy Drive Car Rental within two (2) hours of the incident.",
      "All accidents must be reported immediately to the Royal Bahamas Police Force Traffic Division and to Easy Drive Car Rental.",
      "Vehicle burglary, theft, or vandalism must be reported immediately to the nearest police station and to Easy Drive Car Rental; such incidents are not covered by insurance and remain the renter's financial responsibility.",
      "Damage sustained during the rental period may result in forfeiture of the security deposit in addition to repair charges. No repairs may be undertaken without the prior written consent of Easy Drive Car Rental.",
      "In the event of mechanical failure, battery failure, or tire damage, please contact Easy Drive Car Rental immediately for further instructions.",
      "Optional 24/7 roadside assistance (flat tires, battery jumps, lockouts, and towing support) is available at $15.00 per day. It can be added at the time of booking or mid-rental from your account; mid-rental additions apply to the remaining rental days and become active after a short waiting period.",
    ],
  },
  {
    icon: Fuel,
    title: "Fuel & Mileage",
    testid: "policy-fuel",
    items: [
      "Vehicles are delivered with a full tank of fuel; the exact fuel level (full, three-quarter, half, or quarter) is recorded on the rental agreement at pickup.",
      "Vehicles returned below the recorded fuel level will be refueled at a rate of $8.00 per gallon, with the charge added to the final invoice.",
      "Unlimited mileage on New Providence and Paradise Island is included with every rental.",
    ],
  },
  {
    icon: Car,
    title: "Driving Rules",
    testid: "policy-driving",
    items: [
      "Vehicles must be driven on the left-hand side of the road; Bahamian traffic laws apply at all times.",
      "Vehicles must remain on New Providence and Paradise Island; transport to other islands by ferry is not permitted without prior written approval.",
      "Off-road and beach driving are strictly prohibited.",
      "Seat belts are mandatory for all occupants at all times.",
      "Only registered drivers may operate the vehicle. Operation by an unauthorized driver will result in immediate termination of the rental agreement without refund, and the vehicle will be recovered.",
    ],
  },
  {
    icon: Wrench,
    title: "Vehicle Care & Conduct",
    testid: "policy-care",
    items: [
      "Vehicles must be returned in a clean condition, free of waste or debris; a $30.00 cleaning fee applies to vehicles returned in an unclean condition.",
      "Smoking is strictly prohibited in all vehicles; a $150.00 penalty will be charged.",
      "Vehicles are to be used solely for leisure and light-duty purposes.",
      "No modifications or alterations may be made to the vehicle during the rental period.",
      "For any maintenance needs, please contact Easy Drive Car Rental first; no repairs are permitted without our prior consent.",
      "Vehicles must be returned to the agreed pickup and drop-off location at the time specified in the rental agreement.",
    ],
  },
  {
    icon: Clock,
    title: "Late Returns & Extensions",
    testid: "policy-late",
    items: [
      "Late returns incur a fee of $25.00 per hour unless otherwise arranged in advance.",
      "Replacement of a lost key fob is charged at $250.00 or the dealer's replacement price, whichever is greater; key loss and lockouts are the renter's responsibility.",
      "Gold Voyager members receive one (1) hour of complimentary late return, priority early-pickup reservations, additional drivers at half price, and complimentary vehicle upgrades (subject to availability).",
      "Platinum Islander members receive two (2) hours of complimentary late return, early pickup at no additional charge (subject to vehicle readiness), complimentary additional drivers, and complimentary vehicle upgrades (subject to availability).",
      "Extensions must be added to the existing booking prior to the scheduled return time; otherwise, applicable late fees will be charged to the final invoice.",
      "To extend a rental, please visit your account online or contact us on WhatsApp; all extensions are subject to availability.",
    ],
  },
  {
    icon: MapPin,
    title: "Delivery & Pickup",
    testid: "policy-delivery",
    items: [
      "Drop-off is available at Lynden Pindling International Airport (NAS) for a flat $25 fee, automatically added to your invoice. Complimentary drop-off is offered at Prince George Wharf, Cable Beach, and Paradise Island, as well as hotels and villas across New Providence. Airport pickups are not available at this time.",
      "Provide your flight number at the time of booking and we will monitor any delays — your vehicle will be ready when you arrive.",
    ],
  },
];

const KEY_FEES = [
  { label: "Refundable deposit", value: "$150" },
  { label: "Cancellation charge", value: "20%" },
  { label: "Processing fee", value: "5%" },
  { label: "Airport drop-off", value: "$25" },
  { label: "Toll pass", value: "$8.99 + $3/trip" },
  { label: "Late return", value: "$25/hr" },
];

const PRINT_CSS = `
@media print {
  body { background: #ffffff !important; }
  .no-print { display: none !important; }
  .policy-doc * { color: #000 !important; }
  .policy-section { break-inside: avoid; }
}
`;

function highlight(text) {
  const parts = text.split(/(\$[\d,]+(?:\.\d{1,2})?(?:\s*\+\s*\$[\d,]+(?:\.\d{1,2})?(?:\/\w+)?)?|\d+(?:\.\d+)?%|\b\d+\s*(?:business days|hours?|years?|months?)\b)/g);
  return parts.map((p, i) =>
    /^(\$|\d+(\.\d+)?%|\d+\s)/.test(p) && p.trim() ? (
      <strong key={i} className="text-foreground font-semibold">{p}</strong>
    ) : (
      p
    )
  );
}

export default function Policy() {
  useSEO({ title: "Nassau Car Rental Policies — Deposits & Cancellations | Easy Drive", description: "Easy Drive rental policies: $150 refundable deposit, cancellation charge, fuel and late-return rules, lost key fee and Paradise Island bridge tolls.", path: "/policies" });
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SECTIONS;
    return SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((it) => it.toLowerCase().includes(q) || s.title.toLowerCase().includes(q)) }))
      .filter((s) => s.items.length > 0);
  }, [query]);

  const jumpTo = (testid) => {
    const el = document.getElementById(testid);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="policy-page" className="bg-[#FAF7F2]">
      <style>{PRINT_CSS}</style>

      <div data-testid="policy-hero" className="max-w-3xl mx-auto px-6 pt-32 pb-4 text-center">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary font-semibold">Easy Drive Car Rental · Nassau, Bahamas</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight">
          Rental <span className="italic text-primary font-normal">Policies</span> &amp; Terms
        </h1>
        <div aria-hidden="true" className="mt-6 flex items-center justify-center gap-3">
          <span className="h-px w-16 bg-[#C9A227]/60" />
          <span className="h-1.5 w-1.5 rotate-45 bg-[#C9A227]" />
          <span className="h-px w-16 bg-[#C9A227]/60" />
        </div>
        <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto">
          Everything about renting with Easy Drive — deposits, fuel, insurance, and cancellations —
          written plainly so there are no surprises at pickup.
        </p>
        <div className="no-print mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="policy-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search — try “deposit”"
              className="input-dark pl-9 pr-8 rounded-full bg-white"
            />
            {query && (
              <button data-testid="policy-search-clear" onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors">
                <X size={14} />
              </button>
            )}
          </div>
          <Button data-testid="policy-print-button" variant="outline" onClick={() => window.print()} className="rounded-full border-[#E5DED5] bg-white hover:bg-stone-50 hover:text-primary font-ui shrink-0">
            <Printer size={15} className="mr-2" /> Print / Save PDF
          </Button>
        </div>
      </div>

      <div data-testid="policy-key-stats" className="max-w-3xl mx-auto px-6 mt-10">
        <div className="border-y border-[#C9A227]/30 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {KEY_FEES.map((f) => (
            <div key={f.label} className="text-center">
              <p className="font-display text-xl sm:text-2xl text-foreground">{f.value}</p>
              <p className="text-[10px] font-ui uppercase tracking-[0.18em] text-muted-foreground mt-0.5">{f.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div data-testid="policy-toc-nav" className="no-print max-w-3xl mx-auto px-6 mt-12">
        <p className="text-[10px] font-ui uppercase tracking-[0.25em] text-muted-foreground text-center mb-4">On this page</p>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          {SECTIONS.map(({ title, testid }, i) => (
            <button
              key={testid}
              data-testid={`policy-toc-${testid}`}
              onClick={() => jumpTo(testid)}
              className="group text-sm font-ui text-[#4A4A4A] hover:text-primary transition-colors duration-200 whitespace-nowrap"
            >
              <span className="font-display italic text-[#C9A227] mr-1.5">{String(i + 1).padStart(2, "0")}</span>
              <span className="underline-offset-4 group-hover:underline">{title}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="policy-doc max-w-3xl mx-auto px-6 pt-14 pb-16">
        {visible.length === 0 && (
          <div data-testid="policy-no-results" className="text-center py-16">
            <p className="font-display text-2xl">No policies match “{query}”</p>
            <button onClick={() => setQuery("")} className="mt-3 text-sm font-ui text-primary hover:underline underline-offset-4">Clear search</button>
          </div>
        )}
        {visible.map(({ icon: Icon, title, items, testid }) => {
          const num = String(SECTIONS.findIndex((s) => s.testid === testid) + 1).padStart(2, "0");
          return (
            <section key={testid} id={testid} data-testid={testid} className="policy-section scroll-mt-32 mb-14 last:mb-0">
              <div className="flex items-baseline gap-4">
                <span aria-hidden="true" className="font-display italic text-3xl sm:text-4xl text-primary/90 shrink-0">{num}</span>
                <div className="flex-1 border-b border-[#E5DED5] pb-4">
                  <h2 className="font-display text-2xl sm:text-3xl tracking-tight flex items-center gap-3">
                    {title}
                    <Icon size={18} className="text-[#C9A227] shrink-0" />
                  </h2>
                </div>
              </div>
              <ul className="mt-6 space-y-4 pl-2 sm:pl-14">
                {items.map((item, i) => (
                  <li key={i} className="flex gap-3.5 text-sm sm:text-base text-foreground/80 leading-relaxed">
                    <span aria-hidden="true" className="mt-[11px] h-px w-5 bg-primary/50 shrink-0" />
                    <span>{highlight(item)}</span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <div aria-hidden="true" className="mt-4 mb-14 flex items-center justify-center gap-3">
          <span className="h-px w-16 bg-[#C9A227]/60" />
          <span className="h-1.5 w-1.5 rotate-45 bg-[#C9A227]" />
          <span className="h-px w-16 bg-[#C9A227]/60" />
        </div>

        <div data-testid="policy-contact-banner" className="no-print relative overflow-hidden rounded-2xl bg-[#1A1A1A] p-8 sm:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-[#C9A227]">Questions before you book?</p>
            <p className="mt-3 font-display text-2xl sm:text-3xl text-[#FAF7F2] tracking-tight">We answer within minutes, every day.</p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#FAF7F2]/70 font-ui">
              <span className="inline-flex items-center gap-2"><MessageCircle size={14} className="text-[#C9A227]" /> WhatsApp +1 (242) 813-1775</span>
              <span className="inline-flex items-center gap-2"><Mail size={14} className="text-[#C9A227]" /> bookings@easydrivecarrental.com</span>
            </div>
          </div>
          <Link to="/fleet">
            <Button data-testid="policy-book-button" className="rounded-full bg-primary text-primary-foreground hover:bg-[#A01825] font-ui shrink-0 px-8 h-12">
              Book a Vehicle
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
